import { useEffect, useState } from "react";
import { Save, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { adminGetSettings, adminUpdateSettings } from "./adminApi";
import { useSettings } from "@/context/SettingsContext";
import { Text, Area } from "./ui";

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminGetSettings().then(setS).catch(() => {}); }, []);

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const setSocial = (k, v) => setS((x) => ({ ...x, social: { ...x.social, [k]: v } }));
  const setSeo = (k, v) => setS((x) => ({ ...x, default_seo: { ...x.default_seo, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { await adminUpdateSettings(s); await refresh(); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (!s) return <div className="text-[#5a4b4b]">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><div className="text-[11px] tracking-[0.3em] uppercase text-[#7c1d1d] font-semibold">Manage</div><h1 className="font-serif text-4xl text-[#3b1a1a] mt-1">Settings & SEO</h1></div>
        <button onClick={save} disabled={saving} data-testid="save-settings" className="inline-flex items-center gap-2 bg-[#7c1d1d] text-white px-6 py-3 text-xs tracking-[0.2em] uppercase disabled:opacity-60"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</button>
      </div>

      <div className="space-y-8">
        <Card title="Contact Details">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Text label="Company Name" value={s.company_name} onChange={(v) => set("company_name", v)} />
            <Text label="Primary Phone" value={s.phone} onChange={(v) => set("phone", v)} />
            <Text label="Alternate Phone" value={s.phone_alt} onChange={(v) => set("phone_alt", v)} />
            <Text label="WhatsApp Number" value={s.whatsapp} onChange={(v) => set("whatsapp", v)} />
            <Text label="Email" value={s.email} onChange={(v) => set("email", v)} />
            <Text label="Office Hours" value={s.office_hours} onChange={(v) => set("office_hours", v)} />
          </div>
          <Text label="Office Address" value={s.address} onChange={(v) => set("address", v)} />
          <Text label="Emergency Line" value={s.emergency_line} onChange={(v) => set("emergency_line", v)} />
          <Text label="Default WhatsApp Message" value={s.whatsapp_message} onChange={(v) => set("whatsapp_message", v)} />
          <Text label="Google Map Embed URL" value={s.map_embed} onChange={(v) => set("map_embed", v)} />
        </Card>

        <Card title="Our Offices">
          <p className="text-[11px] text-[#5a4b4b]">Shown as a 3-card grid on /contact. Drag order with arrows.</p>
          <div className="space-y-3">
            {(s.offices || []).map((o, i) => (
              <div key={i} data-testid={`admin-office-${i}`} className="border border-[#d4af37]/25 p-3 bg-[#faf8f5] space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] tracking-[0.22em] uppercase text-[#5a4b4b]">Office {i + 1}</span>
                  <div className="flex items-center gap-2">
                    <button type="button" data-testid={`admin-office-up-${i}`} onClick={() => { const a=[...s.offices]; if(i>0){[a[i-1],a[i]]=[a[i],a[i-1]]; set("offices", a);} }} disabled={i===0} className="text-xs text-[#7c1d1d] disabled:opacity-30">↑</button>
                    <button type="button" data-testid={`admin-office-down-${i}`} onClick={() => { const a=[...s.offices]; if(i<a.length-1){[a[i+1],a[i]]=[a[i],a[i+1]]; set("offices", a);} }} disabled={i===(s.offices?.length||0)-1} className="text-xs text-[#7c1d1d] disabled:opacity-30">↓</button>
                    <button type="button" data-testid={`admin-office-del-${i}`} onClick={() => set("offices", s.offices.filter((_,j)=>j!==i))} className="text-[#7c1d1d]"><X className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Text label="Label" value={o.label} onChange={(v) => set("offices", s.offices.map((x,j)=>j===i?{...x,label:v}:x))} />
                  <Text label="Business Name" value={o.name} onChange={(v) => set("offices", s.offices.map((x,j)=>j===i?{...x,name:v}:x))} />
                </div>
                <Area label="Address" rows={2} value={o.address} onChange={(v) => set("offices", s.offices.map((x,j)=>j===i?{...x,address:v}:x))} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Text label="Phone (optional)" value={o.phone} onChange={(v) => set("offices", s.offices.map((x,j)=>j===i?{...x,phone:v}:x))} />
                  <Text label="Email (optional)" value={o.email} onChange={(v) => set("offices", s.offices.map((x,j)=>j===i?{...x,email:v}:x))} />
                </div>
              </div>
            ))}
          </div>
          <button type="button" data-testid="admin-office-add" onClick={() => set("offices", [...(s.offices||[]), {label:"",name:"Legacy India Travel",address:"",phone:"",email:""}])} className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#7c1d1d]">
            <Plus className="w-3.5 h-3.5" /> Add Office
          </button>
        </Card>

        <Card title="Team & Emergency Contact">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Text label="Director / Contact Person Name" value={s.director_name} onChange={(v) => set("director_name", v)} placeholder="Mr. Haseeb Khan" />
            <Text label="Director / Contact Title" value={s.director_title} onChange={(v) => set("director_title", v)} placeholder="Director" />
            <Text label="Emergency Contact Name" value={s.emergency_contact_name} onChange={(v) => set("emergency_contact_name", v)} placeholder="Mr. Gaurav" />
            <Text label="Emergency Contact Phone" value={s.emergency_contact_phone} onChange={(v) => set("emergency_contact_phone", v)} placeholder="+91 7017798524" />
          </div>
          <p className="text-[11px] text-[#5a4b4b]">Shown on the public Contact page. Emergency contact is displayed in a highlighted 24/7 block.</p>
        </Card>

        <Card title="Social Links">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Text label="Facebook" value={s.social?.facebook} onChange={(v) => setSocial("facebook", v)} />
            <Text label="Instagram" value={s.social?.instagram} onChange={(v) => setSocial("instagram", v)} />
            <Text label="YouTube" value={s.social?.youtube} onChange={(v) => setSocial("youtube", v)} />
            <Text label="Twitter / X" value={s.social?.twitter} onChange={(v) => setSocial("twitter", v)} />
          </div>
        </Card>

        <Card title="Default SEO">
          <Text label="Default Meta Title" value={s.default_seo?.meta_title} onChange={(v) => setSeo("meta_title", v)} />
          <Area label="Default Meta Description" value={s.default_seo?.meta_description} onChange={(v) => setSeo("meta_description", v)} rows={2} />
          <Text label="Default Keywords" value={s.default_seo?.keywords} onChange={(v) => setSeo("keywords", v)} />
        </Card>

        <Card title="Analytics & Verification">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Text label="Google Analytics / GTM ID" value={s.ga_tag} onChange={(v) => set("ga_tag", v)} placeholder="G-XXXX or GTM-XXXX" />
            <Text label="Meta Pixel ID" value={s.meta_pixel} onChange={(v) => set("meta_pixel", v)} />
            <Text label="Google Search Console Verification" value={s.gsc_verification} onChange={(v) => set("gsc_verification", v)} placeholder="Paste the verification token (not the full meta tag)" />
          </div>
          <p className="text-[11px] text-[#5a4b4b]">Sitemap available at <code>/api/sitemap.xml</code> · Robots at <code>/api/robots.txt</code>.</p>
        </Card>

        <Card title="Spam Protection — Google reCAPTCHA v3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Text label="reCAPTCHA v3 Site Key (public)" value={s.recaptcha_site_key} onChange={(v) => set("recaptcha_site_key", v)} placeholder="6L…" />
            <Text label="reCAPTCHA v3 Secret Key (private)" value={s.recaptcha_secret_key} onChange={(v) => set("recaptcha_secret_key", v)} placeholder="6L…" type="password" />
          </div>
          <p className="text-[11px] text-[#5a4b4b]">
            Get free keys at <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="underline">google.com/recaptcha/admin</a> (choose reCAPTCHA v3). Add <code>{typeof window !== "undefined" ? window.location.hostname : "legacyindiatravel.com"}</code> to allowed domains. When both keys are set, all enquiry forms are automatically protected — until then, forms work as normal.
          </p>
        </Card>
      </div>

      <div className="mt-8"><button onClick={save} disabled={saving} className="bg-[#7c1d1d] text-white px-8 py-3 text-xs tracking-[0.2em] uppercase disabled:opacity-60">{saving ? "Saving…" : "Save"}</button></div>
    </div>
  );
}

function Card({ title, children }) {
  return <div className="bg-white border border-[#d4af37]/25 p-6"><h2 className="font-serif text-2xl text-[#3b1a1a] mb-4">{title}</h2><div className="space-y-4">{children}</div></div>;
}
